import { Action, Dispatch } from 'redux';
import toast, { superAdminToast } from '../utils/toast';
import { SelectionModel } from '../cdk/selection';
import { IPublishParams, publishAssets } from '../api/vault/publishAssets';
import { AssetHit } from '../api/vault/searchEngine/interfaces';
import { IPublishSettings, IPublishMetadata, SettingType } from '../api/vault/publishEndpoints';
import { SocialPublish } from '../constants';

export interface IThumbnailFrame {
  clipIndex: number,
  time: number,
}

export interface IState {
  areAssetsPublished: boolean,
  resetAssetsSelected: boolean,
  errorInReset: boolean,
  assetSelection: SelectionModel<AssetHit>
  isSocialPublishDialogOpen: boolean,
  socialPublishing: {
    settings: IPublishSettings<any>,
    metadata: IPublishMetadata,
  },
}

export enum ActionType {
  PublishSelectedSetting = 'PublishAssets.PublishSelectedSetting',
  SelectedSettingsError = 'PublishAssets.SelectedSettingsError',
  ResetPublishSelectedAssets = 'PublishAssets.ResetPublishSelectedAssets',
  PublishedAssetsCannotBeReset = 'PublishAssets.PublishedAssetsCannotBeReset',
  UpdateSelectedAssets = 'PublishAssets.updateSelectedAssets',
  OpenSocialPublishDialog = 'PublishAssets.openSocialPublishDialog',
  CloseSocialPublishDialog = 'PublishAssets.closeSocialPublishDialog',
  InitializeSocialPublishing = 'PublishAssets.initializeSocialPublishing',
  ClearSocialPublishing = 'PublishAssets.clearSocialPublishing',
  UpdateSocialPublishingMetadataField = 'PublishAssets.updateSocialPublishingMetadataField',
}

export interface IPublishAssets extends Action<ActionType> {
  payload?: any
}

const INITIAL_STATE: IState = {
  areAssetsPublished: false,
  resetAssetsSelected: false,
  errorInReset: false,
  assetSelection: new SelectionModel<AssetHit>(),
  isSocialPublishDialogOpen: false,
  socialPublishing: {
    settings: null,
    metadata: null,
  },
};

const EMPTY_SOCIAL_PUBLISHING_METADATA: IPublishMetadata = {
  title: '',
  description: '',
};

const EMPTY_TWITTER_SOCIAL_PUBLISHING_METADATA = {
  ...EMPTY_SOCIAL_PUBLISHING_METADATA,
  whatIsHappening: '',
};

const settingTypeToEmptyMetadataMap = new Map([
  [SettingType.Twitter, EMPTY_TWITTER_SOCIAL_PUBLISHING_METADATA],
]);

export default function reducer(state: IState = INITIAL_STATE, action: IPublishAssets): IState {
  switch (action.type) {
    case ActionType.PublishSelectedSetting:
    case ActionType.SelectedSettingsError:
    case ActionType.ResetPublishSelectedAssets:
    case ActionType.PublishedAssetsCannotBeReset:
    case ActionType.UpdateSelectedAssets:
      return {
        ...state,
        ...action.payload,
      };
    case ActionType.OpenSocialPublishDialog:
      return {
        ...state,
        isSocialPublishDialogOpen: true,
      };
    case ActionType.CloseSocialPublishDialog:
      return {
        ...state,
        isSocialPublishDialogOpen: false,
      };
    case ActionType.InitializeSocialPublishing:
      return {
        ...state,
        socialPublishing: {
          ...state.socialPublishing,
          settings: action.payload,
          metadata: settingTypeToEmptyMetadataMap.has(action.payload.type)
            ? settingTypeToEmptyMetadataMap.get(action.payload.type)
            : EMPTY_SOCIAL_PUBLISHING_METADATA,
        },
      };
    case ActionType.ClearSocialPublishing:
      return {
        ...state,
        socialPublishing: INITIAL_STATE.socialPublishing,
      };
    case ActionType.UpdateSocialPublishingMetadataField:
      return {
        ...state,
        socialPublishing: {
          ...state.socialPublishing,
          metadata: {
            ...state.socialPublishing.metadata,
            [action.payload.fieldName]: action.payload.value,
          },
        },
      };
    default:
      return state;
  }
}

export const publishSelectedAssetsToEndPoints = (publishParams: IPublishParams, isVaultSuperAdmin: boolean) => async (dispatch: Dispatch<IPublishAssets>) => {
  const response = await publishAssets(publishParams);
  if (response.status === 200) {
    dispatch({
      payload: {
        areAssetsPublished: true,
      },
      type: ActionType.PublishSelectedSetting,
    });
    superAdminToast(isVaultSuperAdmin, 'Publish submitted!', { type: toast.TYPE.SUCCESS });
  } else {
    dispatch({
      payload: {
        areAssetsPublished: false,
      },
      type: ActionType.SelectedSettingsError,
    });
    superAdminToast(isVaultSuperAdmin, 'Error during publish', { type: toast.TYPE.ERROR });
  }
};

export const resetPublishedAssets = (resetAssets: boolean) => async (dispatch: Dispatch<IPublishAssets>) => {
  try {
    dispatch({
      payload: {
        resetAssetsSelected: resetAssets,
      },
      type: ActionType.ResetPublishSelectedAssets,
    });
  } catch (e) {
    dispatch({
      payload: {
        errorInReset: true,
      },
      type: ActionType.PublishedAssetsCannotBeReset,
    });
  }
};

export function openSocialPublishDialog() {
  return {
    type: ActionType.OpenSocialPublishDialog,
  };
}

export function closeSocialPublishDialog() {
  return {
    type: ActionType.CloseSocialPublishDialog,
  };
}

export function initializeSocialPublishing(publishSettings: IPublishSettings<any>) {
  return {
    type: ActionType.InitializeSocialPublishing,
    payload: publishSettings,
  };
}

export function clearSocialPublishing() {
  return {
    type: ActionType.ClearSocialPublishing,
  };
}

function truncateIfExceedsMaxLength(fieldName: string, value: any) {
  let newValue = value;
  if (SocialPublish.FIELDS_MAX_LENGTH.has(fieldName)) {
    const maxLength = SocialPublish.FIELDS_MAX_LENGTH.get(fieldName);
    if (value.length > maxLength) {
      newValue = value.slice(0, maxLength);
    }
  }
  return newValue;
}

export function updatePublishingMetadataField(fieldName: string, value: any) {
  const newValue = truncateIfExceedsMaxLength(fieldName, value);

  return {
    type: ActionType.UpdateSocialPublishingMetadataField,
    payload: {
      fieldName,
      value: newValue,
    },
  };
}
